import { Node as ReactFlowNode } from '@xyflow/react';
import {
  // LogOut,
  // Send,
  Target,
  // Users,
  Zap,
  UserCheck,
  Loader2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSegments } from 'store/segments';
import TriggerConditionDisplay from 'components/workflow/TriggerConditionDisplay';
import {
  getCampaignContactCountsRequest,
  selectCampaignContactCounts,
  selectCampaignContactCountsLoading,
} from 'store/campaigns';
import { ICampaign } from 'types';
import { cn } from '../../../utils/cn';

interface ReviewProps {
  campaign?: ICampaign;
  workflow?: any;
  conditions?: { [key: string]: any[] };
  onSelectedTypeChange?: (
    selectedType: 'all_contacts' | 'future_contacts'
  ) => void;
  loading?: boolean;
}

interface ReviewSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  additionalText?: string;
  children?: React.ReactNode;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({
  icon,
  title,
  description,
  additionalText,
  children,
}) => {
  return (
    <div className="flex items-start space-x-3 p-4 border border-border rounded-lg bg-card">
      <div className="flex-shrink-0 w-6 h-6 bg-success-background rounded-md flex items-center justify-center text-success">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-1">{description}</p>
        {additionalText && (
          <p className="text-sm text-muted-foreground mb-2">{additionalText}</p>
        )}
        {children}
      </div>
    </div>
  );
};

// Component to render goal information
const GoalDisplay: React.FC<{ goal?: any }> = ({ goal }) => {
  const segments = useSelector(selectSegments);

  // Helper function to get segment name by ID
  const getSegmentName = (segmentId: string): string => {
    const segment = segments.find((seg) => seg._id === segmentId);
    return segment ? segment.name : segmentId;
  };

  // Helper function to get operator label
  const getOperatorLabel = (operator: string): string => {
    const operatorLabels: { [key: string]: string } = {
      enters_segment: 'Enters segment',
      leaves_segment: 'Leaves segment',
    };
    return operatorLabels[operator] || operator;
  };

  // Helper function to get time unit label
  const getTimeUnitLabel = (unit: string): string => {
    const unitLabels: { [key: string]: string } = {
      day: 'Day',
      week: 'Week',
      month: 'Month',
    };
    return unitLabels[unit] || unit;
  };

  // Helper function to get event label
  const getEventLabel = (event: string): string => {
    const eventLabels: { [key: string]: string } = {
      sent: 'Being sent',
      'being sent': 'Being sent',
      opened: 'Opening',
      clicked: 'Clicking a tracked link in',
    };
    return eventLabels[event.toLowerCase()] || event;
  };

  // Check if goal is set
  if (!goal || goal.type === 'no_goal') {
    return (
      <ReviewSection
        icon={<Target className="w-4 h-4 text-success" />}
        title="Goal"
        description="No goal set."
      />
    );
  }

  // Goal is set, display details
  const { condition, timeDuration } = goal;
  const segmentName = condition?.value
    ? getSegmentName(condition.value)
    : 'Unknown segment';
  const operatorLabel = condition?.operator
    ? getOperatorLabel(condition.operator)
    : '';
  const duration = timeDuration?.duration || 1;
  const unitLabel = timeDuration?.unit
    ? getTimeUnitLabel(timeDuration.unit)
    : '';
  const eventLabel = timeDuration?.event
    ? getEventLabel(timeDuration.event)
    : '';

  return (
    <ReviewSection
      icon={<Target className="w-4 h-4 text-success" />}
      title="Goal"
      description=""
    >
      <div className="space-y-4 mt-3">
        {/* Conversion criteria */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-1">
            Conversion criteria
          </h4>
          <p className="text-sm text-muted-foreground mb-2">
            People meet the conversion criteria when they:
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-foreground">{operatorLabel}</span>
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-muted border border-border rounded-full">
              <div className="w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                <div className="w-1.5 h-1.5 bg-primary-foreground rounded-full"></div>
              </div>
              <span className="font-medium text-foreground">{segmentName}</span>
            </div>
            <span className="text-muted-foreground">segment</span>
          </div>
        </div>

        {/* Conversion window */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-1">
            Conversion window
          </h4>
          <p className="text-sm text-muted-foreground mb-2">
            A conversion is counted when a person:
          </p>
          <div className="text-sm text-foreground">
            <span>Achieves the goal within </span>
            <span className="font-medium">{duration}</span>
            <span> {unitLabel.toLowerCase()}</span>
            <span> of </span>
            <span className="font-medium">{eventLabel}</span>
            <span> any delivery from this campaign.</span>
          </div>
        </div>
      </div>
    </ReviewSection>
  );
};

const Review: React.FC<ReviewProps> = ({
  campaign,
  workflow,
  conditions,
  onSelectedTypeChange,
  loading = false,
}) => {
  const dispatch = useDispatch();
  const [includeExistingMatches, setIncludeExistingMatches] = useState<
    boolean | null
  >(null);
  const [triggerConditions, setTriggerConditions] = useState<any>(null);
  const [isEventTrigger, setIsEventTrigger] = useState(false);
  const [triggerEventName, setTriggerEventName] = useState('');
  const contactCounts = useSelector(selectCampaignContactCounts);
  const contactCountsLoading = useSelector(selectCampaignContactCountsLoading);

  // useEffect(() => {
  //   if (campaign) {
  //     setIncludeExistingMatches(
  //       campaign.type === 'all_contacts' || !campaign.type
  //     );
  //   }
  // }, [campaign]);

  useEffect(() => {
    let nextIsEventTrigger = campaign?.triggerType === 'event';
    let nextTriggerEventName = campaign?.event || '';
    let nextTriggerConditions: any = null;

    if (conditions && Object.keys(conditions).length > 0) {
      nextTriggerConditions = conditions;
    }

    if (workflow?.draftFlow?.nodes) {
      const triggerNode = workflow.draftFlow.nodes.find(
        (node: ReactFlowNode) => node.type === 'trigger'
      );
      const triggerConfig = (triggerNode?.data as any)?.config || {};

      if (triggerNode) {
        nextIsEventTrigger =
          triggerConfig.trigger === 'event' || nextIsEventTrigger;
        nextTriggerEventName = triggerConfig.event || nextTriggerEventName;

        if (
          triggerConfig.conditions &&
          Object.keys(triggerConfig.conditions).length > 0
        ) {
          nextTriggerConditions = triggerConfig.conditions;
        }
      }
    }

    setIsEventTrigger(nextIsEventTrigger);
    setTriggerEventName(nextTriggerEventName);
    setTriggerConditions(nextTriggerConditions);
  }, [workflow, conditions, campaign?.triggerType, campaign?.event]);

  // Fetch contact counts when campaign is available and selectedType is all_contacts
  useEffect(() => {
    if (campaign?._id && includeExistingMatches && !isEventTrigger) {
      dispatch(getCampaignContactCountsRequest(campaign._id));
    }
  }, [campaign?._id, includeExistingMatches, isEventTrigger, dispatch]);

  useEffect(() => {
    if (isEventTrigger) return;
    const selectedType = includeExistingMatches
      ? 'all_contacts'
      : 'future_contacts';
    if (onSelectedTypeChange) {
      onSelectedTypeChange(selectedType);
    }
  }, [includeExistingMatches, isEventTrigger, onSelectedTypeChange]);

  // Calculate message count
  // const getMessageCount = () => {
  //   if (!workflow?.draftFlow?.nodes) return 0;
  //   return workflow.draftFlow.nodes.filter(
  //     (node: any) => node.type === 'action' && node.data?.type === 'email'
  //   ).length;
  // };

  // const messageCount = getMessageCount();

  return (
    <div className="flex h-[calc(90vh-40px)]">
      <div className="flex-1 overflow-y-auto bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-1">
                Review and {campaign?.status === 'live' ? 'Update' : 'Start'}{' '}
                Campaign
              </h2>
              <p className="text-sm text-muted-foreground">
                Review your campaign before{' '}
                {campaign?.status === 'live' ? 'updating' : 'starting'}{' '}
                {campaign?.name || 'Untitled Campaign'}.
              </p>
            </div>

            {/* Trigger Section */}
            <ReviewSection
              icon={<Zap className="w-4 h-4 text-success" />}
              title="Trigger"
              description={
                isEventTrigger
                  ? 'People will enter this campaign every time they perform the event:'
                  : 'People will enter this campaign the first time they meet the following conditions.'
              }
            >
              <div className="space-y-3 mt-3">
                <TriggerConditionDisplay
                  conditions={triggerConditions || {}}
                  triggerType={isEventTrigger ? 'event' : 'attribute-segment'}
                  eventName={triggerEventName}
                />

                {!isEventTrigger && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Would you like your campaign to include existing matches,
                      or start fresh?
                    </p>

                    <div className="flex gap-2">
                      <div
                        className={cn(
                          'p-2 border rounded-md cursor-pointer transition-colors',
                          includeExistingMatches
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:bg-muted/50'
                        )}
                        onClick={() => {
                          setIncludeExistingMatches(true);
                        }}
                      >
                        <div className="flex items-start space-x-2">
                          <input
                            type="radio"
                            checked={includeExistingMatches ?? false}
                            onChange={() => {
                              setIncludeExistingMatches(true);
                            }}
                            className="mt-0.5 accent-primary border-border focus:ring-0"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              Current people and future additions
                            </p>
                            <p className="text-sm text-muted-foreground">
                              The people who currently match the trigger
                              conditions will enter your campaign.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        className={cn(
                          'p-2 border rounded-md cursor-pointer transition-colors',
                          includeExistingMatches !== null &&
                            !includeExistingMatches
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:bg-muted/50'
                        )}
                        onClick={() => {
                          setIncludeExistingMatches(false);
                        }}
                      >
                        <div className="flex items-start space-x-2">
                          <input
                            type="radio"
                            checked={
                              includeExistingMatches !== null &&
                              !includeExistingMatches
                            }
                            onChange={() => {
                              setIncludeExistingMatches(false);
                            }}
                            className="mt-0.5 accent-primary border-border focus:ring-0"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              Future additions only
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Only people who match trigger conditions after the
                              campaign has started will enter your campaign.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!isEventTrigger && includeExistingMatches && (
                  <div className="mt-4 pt-4 border-t border-border">
                    {contactCountsLoading ? (
                      <div className="flex items-center gap-3 p-3 bg-info-background border border-info/30 rounded-lg">
                        <Loader2 className="w-5 h-5 text-info animate-spin flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            Calculating contact count...
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            This may take a few moments
                          </p>
                        </div>
                      </div>
                    ) : contactCounts?.totalContacts !== undefined ? (
                      <div className="flex items-start gap-2 p-3 bg-success-background border border-success/30 rounded-lg shadow-sm">
                        <div className="flex-shrink-0 w-8 h-8 bg-success-background rounded-lg flex items-center justify-center text-success">
                          <UserCheck className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-xl font-bold text-success">
                              {contactCounts.totalContacts.toLocaleString()}
                            </span>
                            <span className="text-sm font-semibold text-success">
                              {contactCounts.totalContacts === 1
                                ? 'person'
                                : 'contacts'}{' '}
                              {contactCounts.totalContacts === 1
                                ? 'matches'
                                : 'match'}{' '}
                              the trigger conditions
                            </span>
                          </div>
                          {contactCounts.totalContacts > 0 && (
                            <p className="text-xs text-muted-foreground mt-1.5">
                              These contacts will enter your campaign when it
                              starts
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg">
                        <div className="w-5 h-5 rounded-full bg-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Contact count unavailable
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Unable to calculate the number of matching contacts
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  {isEventTrigger
                    ? 'People will exit the campaign early if they stop matching the filters.'
                    : 'People will exit the campaign early if they stop matching the trigger.'}
                </p>
              </div>
            </ReviewSection>

            {/* Messages Section */}
            {/* <ReviewSection
              icon={<Send className="w-4 h-4 text-success" />}
              title="Messages"
              description="Messages will be sent to: All subscribed people"
              additionalText="Message limit is OFF"
            /> */}

            {/* Workflow Section */}
            {/* <ReviewSection
              icon={<Users className="w-4 h-4 text-success" />}
              title="Workflow"
              description={`${messageCount} message${messageCount !== 1 ? 's' : ''} sending to all subscribed and unsubscribed. Sending to unsubscribed customers can have consequences.`}
            >
              <p className="text-sm text-muted-foreground mt-2">
                <span className="underline cursor-pointer hover:text-foreground">
                  can have consequences
                </span>
              </p>
            </ReviewSection> */}

            {/* Goal Section */}
            <GoalDisplay goal={campaign?.goal} />

            {/* Exit Section */}
            {/* <ReviewSection
              icon={<LogOut className="w-4 h-4 text-success" />}
              title="Exit"
              description="A person will exit the campaign early when they stop matching the trigger."
            /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;
